import React, { useCallback, useEffect, useId, useState } from 'react'

export type UseComboboxProps = {
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onSelect?: (value: string, selected: boolean) => void
  options: { value: string; label: React.ReactNode; selected: boolean; disabled?: boolean }[]
  multiSelect?: boolean
  disabled?: boolean
}

export function useCombobox({ onChange, onBlur, onSelect, options, multiSelect, disabled }: UseComboboxProps) {
  const optionIdPrefix = useId()

  const [open, setOpen] = useState(false)
  const [activeOption, setActiveOptionValue] = useState<{ idx: number; value: string } | null>(null)
  const setActiveOption = useCallback(
    (value: typeof activeOption) => {
      setActiveOptionValue(value)
      if (value) {
        document.querySelector(`[id="${optionIdPrefix}${value.value}"]`)?.scrollIntoView({ block: 'nearest' })
      }
    },
    [setActiveOptionValue]
  )

  const handleInputBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setOpen(false)
      setActiveOption(null)
      onBlur?.(e)
    },
    [setOpen, setActiveOption, onBlur]
  )

  const handleInputClick = useCallback(() => setOpen(true), [setOpen])

  const handleInputChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setOpen(true)
      onChange?.(e)
    },
    [setOpen, onChange]
  )

  const handleInputKeydown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()

        if (!open) {
          setOpen(true)
        }

        if (!options.length) {
          setActiveOption(null)
          return
        }

        let idx = activeOption ? activeOption.idx : -1

        if (e.key === 'ArrowUp') {
          idx = (idx - 1 + options.length) % options.length
        } else if (e.key === 'ArrowDown') {
          idx = (idx + 1) % options.length
        } else if (['Home', 'PageUp'].includes(e.key)) {
          idx = 0
        } else if (['End', 'PageDown'].includes(e.key)) {
          idx = options.length - 1
        }

        setActiveOption({ idx, value: options[idx].value })
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        setActiveOption(null)
      } else if (['Space', 'Enter'].includes(e.code) && activeOption) {
        e.preventDefault()
        e.stopPropagation()
        if (!options[activeOption.idx].disabled) {
          onSelect?.(activeOption.value, !options[activeOption.idx].selected)
          if (!multiSelect) {
            setOpen(false)
            setActiveOption(null)
          }
        }
      } else {
        setActiveOption(null)
      }
    },
    [multiSelect, setOpen, open, setActiveOption, activeOption, options, onSelect]
  )

  const handleListMouseDown = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      const idxValue = (e.target as any)?.closest?.('[data-idx]')?.dataset.idx
      if (idxValue) {
        const idx = parseInt(idxValue, 10)
        const option = options[idx]
        setActiveOption({ idx, value: option.value })
        if (!option.disabled) {
          onSelect?.(option.value, !option.selected)
          if (!multiSelect) {
            setOpen(false)
            setActiveOption(null)
          }
        }
      }
    },
    [multiSelect, setActiveOption, options]
  )

  useEffect(() => {
    if (activeOption && activeOption.value !== options[activeOption.idx]?.value) {
      if (!options.length) {
        setActiveOption(null)
      } else {
        let idx = options.findIndex(({ value }) => value === activeOption.value)
        if (idx === -1) {
          idx = activeOption.idx
        }
        idx = Math.max(0, Math.min(options.length - 1, idx))
        setActiveOption({ idx, value: options[idx].value })
      }
    }
  }, [options])

  const getOptionProps = useCallback(
    ({ value, idx }: { value: string; idx: number }) => ({
      id: `${optionIdPrefix}${value}`,
      'data-idx': idx,
    }),
    [optionIdPrefix, activeOption]
  )

  return {
    dropdownId: optionIdPrefix,
    open,
    activeOption,
    activeOptionId: open && activeOption ? `${optionIdPrefix}${activeOption.value}` : undefined,
    inputProps: {
      onChange: handleInputChange,
      onKeyDown: handleInputKeydown,
      onBlur: handleInputBlur,
      onClick: handleInputClick,
      disabled,
      autoComplete: 'off',
    },
    listProps: {
      onMouseDown: handleListMouseDown,
    },
    getOptionProps,
  }
}
